import { t } from '@ctcells/i18n'

import type { Column } from '../../atoms/DataTable.tsx'
import { sortRows, type TableSort } from '../../atoms/tableSort.ts'
import { CSV_BOM, csvCell } from '../batches/eventLogCsv.ts'
import { fullDateTimeText } from '../format.ts'

/**
 * **관리 › 로그 「내보내기」** — 지금 보고 있는 탭의 표를 **화면과 같은 열 · 같은 행**으로 CSV 한 장에 담는다
 * (eric 2026-10-14 「관리에서 내보내기 버튼을 클릭하는데.. 왜 버튼이 안먹어?」 — 핸들러 없는 죽은 단추였다).
 *
 * 🔴 **받은 것만 적는다** — 새 요청이 없다. 검색으로 걸러진 행이면 걸러진 그대로다(화면에 보이는 것이 곧 파일이다).
 * 🔴 열은 표의 열 차례 · 제목 그대로다. 칸 글자는 `exportValue` → `sortValue`(시각은 초까지 · 현장 시간대) 차례로 읽는다.
 *    공간이 모자라 화면에서 빠진 열(`priority`)도 파일에는 다 들어간다.
 * 🔴 행은 표의 처음 정렬(시각 최신 위)이다.
 * ⚠ 서버 내보내기(`POST /exports`)를 쓰지 않는 까닭: 그 데이터셋에 이 탭들(이벤트 · 보충 · 폐기통 교체)이 없고, 감사 · 앱 로그는
 *   반출 사유가 필수인 비동기 작업이다. 화면 목록 그대로를 받는 쪽이 「지금 보는 것」과 어긋나지 않는다.
 */
export function logsCsv<Row>(input: {
  title: string
  columns: readonly Column<Row>[]
  rows: readonly Row[]
  sort: TableSort
  asOf: Date
}): string {
  const { columns, sort } = input
  const ordered = sortRows(input.rows, columns, sort)
  const header = columns.map((column) => column.header)
  const body = ordered.map((row) => columns.map((column) => cellText(column, row)))
  const head = [
    [t('logs.export.title'), input.title],
    [t('logs.export.asOf'), fullDateTimeText(input.asOf.toISOString())],
    [t('logs.export.rowCount'), String(ordered.length)],
  ]
  const lines = [...head.map(line), '', line(header), ...body.map(line)]
  return CSV_BOM + lines.join('\r\n') + '\r\n'
}

function line(cells: readonly string[]): string {
  return cells.map(csvCell).join(',')
}

function cellText<Row>(column: Column<Row>, row: Row): string {
  if (column.exportValue !== undefined) return column.exportValue(row)
  if (column.sortValue === undefined) return ''
  const value = column.sortValue(row)
  if (value === null || value === undefined) return ''
  if (column.sort === 'time' && typeof value === 'string') return fullDateTimeText(value)
  return String(value)
}

/** 파일 이름에 못 쓰는 글자(윈도우 기준 · 제어 문자 포함) */
// eslint-disable-next-line no-control-regex -- 제어 문자를 걸러 내는 것이 이 식의 일이다
const UNSAFE_FILE_CHARS = /[\\/:*?"<>|\u0000-\u001f]/g

/** `로그_<탭>_YYYYMMDD-HHmmss.csv` — 시각은 현장 시간대 */
export function logsFileName(tabLabel: string, at: Date): string {
  const stamp = fullDateTimeText(at.toISOString())
    .replaceAll('-', '')
    .replaceAll(':', '')
    .replace(' ', '-')
  const tab = tabLabel.replace(UNSAFE_FILE_CHARS, '_').trim()
  return `${t('logs.export.fileTag')}_${tab}_${stamp}.csv`
}
